// Matt or glossy — a fact of the INSTALLED glass unit (two of the same product
// can be different finishes), so it lives on the install row:
// DevicesInstalled.GlassFinish  0 = matt (default), 1 = glossy.
// Column added by Home Automation glass_finish_migration.sql (run once).
//
// ⚠️ The room-device lists name their columns and do not carry GlassFinish, so
// it is read from the full install row (`select a.*`, the same call the Device
// Details sheet uses) once per device and cached for the session — a room of
// plates must not re-ask on every render.
//
// Before the migration has run, a read finds no column (matt, the default) and
// a write fails — `setGlassFinish` says so rather than pretending.

import { getDeviceDetail, getDeviceInstalledById, updateDeviceInstalled } from '../api/api';

export const FINISH = { MATT: 'matt', GLOSSY: 'glossy' };

const cache = new Map();        // uno -> 'matt' | 'glossy'
const inflight = new Map();     // uno -> Promise
const listeners = new Set();

export const finishOfRow = (row) => (Number(row && row.GlassFinish) === 1 ? FINISH.GLOSSY : FINISH.MATT);

/** Cached finish for a device, or undefined if not read yet. */
export const cachedFinish = (uno) => cache.get(String(uno));

/** -> 'matt' | 'glossy'. Never throws; matt when it cannot be read. */
export function getGlassFinish(device) {
  const uno = String(device && device.UniqueNumber);
  if (cache.has(uno)) return Promise.resolve(cache.get(uno));
  if (inflight.has(uno)) return inflight.get(uno);
  const p = (async () => {
    try {
      let id = device && device.ID;
      if (id == null) {
        const res = await getDeviceDetail(uno);
        id = (Array.isArray(res.data) ? res.data[0] : null)?.ID;
      }
      if (id == null) return FINISH.MATT;
      const res = await getDeviceInstalledById(id);
      const row = Array.isArray(res.data) ? res.data[0] : null;
      const f = finishOfRow(row);
      cache.set(uno, f);
      return f;
    } catch (e) {
      return FINISH.MATT;
    } finally {
      inflight.delete(uno);
    }
  })();
  inflight.set(uno, p);
  return p;
}

/** Plates re-draw when any finish changes. -> unsubscribe */
export function onFinishChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** -> { ok, message? } */
export async function setGlassFinish(uniqueNumber, finish) {
  const uno = String(uniqueNumber);
  try {
    const res = await updateDeviceInstalled(uno, { GlassFinish: finish === FINISH.GLOSSY ? 1 : 0 });
    const d = res && res.data;
    // The generic update answers a missing column with an error object, not a throw.
    if (d && (d.err || d.status === '500' || d.message === 'some error occured')) {
      return { ok: false, message: 'The server does not have the Finish column yet (glass_finish_migration.sql).' };
    }
    cache.set(uno, finish);
    listeners.forEach((fn) => { try { fn(uno, finish); } catch (e) { /* a listener's fault */ } });
    return { ok: true };
  } catch (e) {
    if (e && e.response && e.response.status === 500) {
      return { ok: false, message: 'The server does not have the Finish column yet (glass_finish_migration.sql).' };
    }
    return { ok: false, message: 'Could not save the finish. Try again.' };
  }
}
